import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { dashboardApi, budgetsApi, kpiApi, storesApi, areaManagersApi, costsApi, storeNotesApi, aiApi, inventoryApi, ticketsApi, staffApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import DateRangePicker from '../components/DateRangePicker';
import { StoreStationsPanel } from '../components/StoreStationsPanel';
import { StoreStationsEditor } from '../components/StoreStationsEditor';
import { rangeToPeriod, periodToRange, isCanonicalRange } from '../lib/dateRangeUtils';
import { parseISO, format as dfFormat } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import CalendarTab from '../components/CalendarTab';
import ComplianceTab from '../components/compliance/ComplianceTab';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { AIInsightsPanel } from '../components/AIInsightsPanel';
import GoogleReviewsPanel from '../components/GoogleReviewsPanel';
import { CopyLinkButton } from '../components/CopyLinkButton';
import { SeriesTooltipContent } from '../components/SeriesTooltipContent';
import { semaphoreFromPct, semaphoreFromPp, SEMAPHORE_TEXT } from '../lib/semaphore';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import ProfitabilitySection from '../components/ProfitabilitySection';
import StoreKpiPanel, { HealthScoreHeader } from '../components/StoreKpiPanel';
// HealthTrendChart removed from Store Dashboard (idea_0d76ebfb1cb2, 2026-05-08):
// the Andamento Mensile (AnnualSparkline) now sits in its place as the
// primary trend visualization. Component kept in the codebase for possible
// future re-use (e.g., dedicated Health page).
import {
  ArrowLeft, TrendingUp, TrendingDown, Minus, DollarSign, Users, Clock, Star,
  Save, BarChart3, Wallet, Settings, Plus, Trash2, Receipt, StickyNote, Pencil, Info, AlertTriangle, CalendarDays, Sparkles, ExternalLink, Target, Package, Wrench, IdCard, Mail, Phone, Armchair, ShieldCheck
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, Legend, ReferenceLine } from 'recharts';
import { usePersistedPeriod } from '../lib/usePersistedPeriod';

const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const KPI_ICONS = { revenue: DollarSign, efficiency: Clock, client: Users, quality: Star, count: TrendingUp, capacity: Clock };

// Formule per i KPI derivati: tooltip esplicativi accanto al label.
const KPI_FORMULAS = {
  hourly_revenue: 'Ricavi totali / Ore disponibili',
  avg_ticket: 'Ricavi totali / Numero servizi',
  occupancy_pct: 'Ore prenotate / Ore disponibili',
  new_client_pct: 'Nuovi clienti / Totale clienti',
  returning_client_rate: 'Clienti abituali / Totale clienti',
};

function fmt(val, format) {
  if (val == null) return '-';
  if (format === 'currency') return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);
  if (format === 'percentage') return `${val.toFixed(1)}%`;
  if (format === 'hours') return `${val.toFixed(1)}h`;
  if (format === 'integer') return Math.round(val).toLocaleString('it-IT');
  return val.toLocaleString('it-IT', { maximumFractionDigits: 2 });
}

function SemDot({ status }) {
  return <div className={`w-2 h-2 rounded-full sem-dot-${status}`} />;
}

function VarCell({ value, format, semaforo }) {
  if (value == null) return <td className="num text-slate-300">-</td>;
  const color = semaforo === 'green' ? 'text-emerald-700' : semaforo === 'red' ? 'text-red-700' : 'text-amber-700';
  const prefix = value > 0 ? '+' : '';
  return (
    <td className={`num ${color} font-medium`}>
      <div className="flex items-center justify-end gap-1.5">
        <SemDot status={semaforo} />
        <span>{prefix}{fmt(value, format)}</span>
      </div>
    </td>
  );
}

export default function StoreDashboardPage() {
  const { storeId } = useParams();
  const { user } = useAuth();
  // Il tab Budget è OPS-only (richiesta utente 2026-05-08). AM e Store User
  // non lo vedono nella tab list e se ci approdano via deep link
  // `?tab=budget` vengono rimbalzati su `dashboard`. Il backend rifiuta
  // comunque `/api/budgets*` con 403 (vedi routers/kpi.py).
  const isOps = user?.role === 'operations_manager';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // period encodes the view: "1".."12" for months, "Q1".."Q4", "YEAR".
  // Resta la source-of-truth per i widget mensili (MonthSelectorRich) e i
  // deep-link cross-pagina; il `DateRangePicker` può scegliere un range
  // custom che non corrisponde a un period canonico → in quel caso
  // `customRange` non è null e il backend riceve `date_from/date_to`.
  const { period, setPeriod, year, setYear } = usePersistedPeriod('1', 2026);
  const [customRange, setCustomRange] = useState(null); // { from, to } se non canonico
  const [activeTab, setActiveTab] = useState(() => {
    // Deep link support: ?tab=inventory|costs|notes|budget|dashboard
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get('tab');
    const allowed = ['dashboard', 'calendar', 'budget', 'costs', 'notes', 'inventory', 'team', 'details', 'stations', 'compliance'];
    return allowed.includes(t) ? t : 'dashboard';
  });
  const [refreshKey, setRefreshKey] = useState(0);

  // Derived period params passed to both backend and children
  const isQuarter = period.startsWith('Q');
  const isAnnual = period === 'YEAR';
  const month = useMemo(() => (isQuarter || isAnnual ? null : Number(period)), [period, isQuarter, isAnnual]);
  const quarter = useMemo(() => (isQuarter ? Number(period.slice(1)) : null), [period, isQuarter]);
  const effectiveRange = useMemo(() => {
    if (customRange?.from && customRange?.to) return customRange;
    return periodToRange(period, year) || null;
  }, [customRange, period, year]);
  const periodLabel = useMemo(() => {
    if (customRange?.from && customRange?.to && !isCanonicalRange(customRange.from, customRange.to)) {
      try {
        const f = parseISO(customRange.from);
        const t = parseISO(customRange.to);
        return `${dfFormat(f, 'd MMM', { locale: itLocale })} → ${dfFormat(t, 'd MMM yy', { locale: itLocale })}`;
      } catch {
        return `${customRange.from} → ${customRange.to}`;
      }
    }
    if (isAnnual) return `Anno ${year}`;
    if (isQuarter) return `Q${quarter} ${year}`;
    return `${MONTHS[month - 1]} ${year}`;
  }, [customRange, isAnnual, isQuarter, month, quarter, year]);

  const refreshData = useCallback(() => {
    setRefreshKey(k => k + 1);
    // Segnala a eventuali Dashboard Area / Panoramica aperte (in tab diverse o
    // nel routing) che i dati sono cambiati e vanno ricaricati.
    window.dispatchEvent(new CustomEvent('storeops:data-changed', { detail: { storeId } }));
  }, [storeId]);

  // Auto-refresh quando la tab torna visibile (es. import costi in altra tab)
  useEffect(() => {
    const bump = () => setRefreshKey(k => k + 1);
    const onVis = () => { if (document.visibilityState === 'visible') bump(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('storeops:data-changed', bump);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('storeops:data-changed', bump);
    };
  }, []);

  // OPS-only guard sul tab Budget: se un AM/Store User atterra su
  // activeTab='budget' (es. via deep link o stato salvato), lo rimbalziamo
  // su 'dashboard'. Lo facciamo dentro useEffect per non far flickerare la UI.
  useEffect(() => {
    if (!isOps && activeTab === 'budget') setActiveTab('dashboard');
  }, [isOps, activeTab]);

  useEffect(() => {
    setLoading(true);
    const params = { year };
    // Pass through del range. Il backend autodetecta i range canonici e
    // sceglie pipeline mensile vs daily.
    if (effectiveRange?.from && effectiveRange?.to) {
      params.date_from = effectiveRange.from;
      params.date_to = effectiveRange.to;
    } else if (isAnnual) {
      params.annual = true;
    } else if (isQuarter) {
      params.quarter = quarter;
    } else {
      params.month = month;
    }
    dashboardApi.store(storeId, params)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [storeId, effectiveRange, month, quarter, isAnnual, isQuarter, year, refreshKey]);

  // Drill-down highlight: la query string `?kpi=<key>` (passata dall'Overview)
  // fa scrollare il KPI corrispondente nel viewport e lo evidenzia con un
  // flash giallo per ~1.6s. Non muta lo state, è puro DOM side-effect, e
  // viene riarmato ad ogni cambio di `kpi` o di `data` (dopo un refetch
  // della tabella i nodi sono nuovi, vanno re-target-ati).
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightKpi = searchParams.get('kpi');
  useEffect(() => {
    if (!highlightKpi || !data) return;
    // Aspetta un tick perché la tabella KPI viene montata sul tab "Dashboard"
    // dopo che `data` è disponibile.
    const timer = setTimeout(() => {
      const node = document.querySelector(`[data-testid="kpi-row-${highlightKpi}"]`);
      if (!node) return;
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      node.classList.add('kpi-row-flash');
      setTimeout(() => node.classList.remove('kpi-row-flash'), 1800);
      // Pulisci il param dalla URL per evitare che un refresh ri-flashi.
      const next = new URLSearchParams(searchParams);
      next.delete('kpi');
      setSearchParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(timer);
  }, [highlightKpi, data, searchParams, setSearchParams]);

  if (!data) {
    // Show spinner only on the very first load so that refresh cycles
    // (visibilitychange / storeops:data-changed) don't unmount child panels
    // and cause redundant re-fetches.
    return <div className="p-8"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;
  }
  const { store } = data;

  return (
    <div className="p-4 md:p-8 max-w-[1920px]" data-testid="store-dashboard">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4 gap-3">
        <div className="min-w-0">
          <Link to="/stores" className="text-sm text-slate-400 hover:text-black flex items-center gap-1 mb-2" data-testid="back-to-stores">
            <ArrowLeft className="w-3 h-3" /> Negozi
          </Link>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900" data-testid="store-name">{store.display_name}</h1>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="text-sm text-slate-500">{store.brand_name}</span>
            {store.area_manager_name && <><span className="text-slate-300">|</span><span className="text-sm text-slate-500">{store.area_manager_name}</span></>}
            {store.area && <><span className="text-slate-300">|</span><span className="text-sm text-slate-400">{store.area}</span></>}
            <span className="text-slate-300">|</span><span className="font-mono text-xs text-slate-400">{store.code}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          <CopyLinkButton testId="store-copy-link" />
          {/* Date range picker — source-of-truth della fetch. Quando il
              range coincide con un periodo canonico aggiorniamo anche
              `period`/`year` per i deep-link e il MonthSelectorRich.
              Range custom (non canonici) → backend daily-aggregation
              (refactor 2026-05-13, idea_330bfb10f8d7). */}
          <DateRangePicker
            from={(effectiveRange || {}).from || ''}
            to={(effectiveRange || {}).to || ''}
            onChange={(f, t) => {
              if (isCanonicalRange(f, t)) {
                const conv = rangeToPeriod(f, t);
                setPeriod(conv.period);
                setYear(conv.year);
                setCustomRange(null);
              } else {
                setCustomRange({ from: f, to: t });
              }
            }}
            testIdPrefix="store-daterange"
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="rounded-none bg-transparent border-b border-slate-200 h-auto p-0 gap-0 w-full overflow-x-auto justify-start whitespace-nowrap">
          {[
            { v: 'dashboard', icon: BarChart3, label: 'Dashboard' },
            { v: 'calendar', icon: CalendarDays, label: 'Giornaliero' },
            // Budget tab is OPS-only — AM/Store User don't even see it.
            ...(isOps ? [{ v: 'budget', icon: Wallet, label: 'Budget' }] : []),
            { v: 'costs', icon: Receipt, label: 'Costi' },
            { v: 'inventory', icon: Package, label: 'Inventario' },
            { v: 'stations', icon: Armchair, label: 'Postazioni' },
            { v: 'team', icon: IdCard, label: 'Squadra' },
            // Conformità: compilata da manager/responsabili → visibile a OPS e AM.
            ...(isOps || user?.role === 'area_manager' ? [{ v: 'compliance', icon: ShieldCheck, label: 'Conformità' }] : []),
            { v: 'details', icon: Settings, label: 'Dettagli' },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-2.5" data-testid={`tab-${t.v}`}>
              <t.icon className="w-4 h-4 mr-2" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="dashboard">
          <DashboardTab
            data={data} month={month} year={year} quarter={quarter} annual={isAnnual}
            periodLabel={periodLabel} period={period}
            storeId={storeId} onPeriodChange={setPeriod}
            isOps={isOps} store={store}
          />
        </TabsContent>
        <TabsContent value="calendar">
          <CalendarTab storeId={storeId} storeName={store.display_name}
            initialMonth={month || (quarter ? (quarter - 1) * 3 + 1 : new Date().getMonth() + 1)}
            initialYear={year}
            // Quando l'utente naviga tra i mesi col selettore del calendario
            // (frecce prev/next), aggiorniamo il filtro periodo dell'header
            // così le altre tab restano allineate (refactor 2026-05-08,
            // idea_0bbfb95e4434). Se era attivo un range custom (refactor
            // 2026-05-13, daily-first), lo cancelliamo qui: il calendario
            // lavora sempre per mese canonico, quindi la navigazione tramite
            // frecce deve riportare il filtro a un mese pulito.
            onMonthYearChange={(m, y) => {
              if (customRange) setCustomRange(null);
              if (String(m) !== period) setPeriod(String(m));
              if (y !== year) setYear(y);
            }}
          />
        </TabsContent>
        {isOps && (
          <TabsContent value="budget">
            <BudgetTab
              storeId={storeId} storeName={store.display_name} year={year}
              month={month || (quarter ? (quarter - 1) * 3 + 1 : 1)}
              onMonthChange={(m) => setPeriod(String(m))} onChanged={refreshData}
            />
          </TabsContent>
        )}
        <TabsContent value="costs"><CostsTab storeId={storeId} storeName={store.display_name} onChanged={refreshData} /></TabsContent>
        <TabsContent value="inventory"><InventoryTab storeId={storeId} storeName={store.display_name} /></TabsContent>
        <TabsContent value="stations"><StoreStationsPanel storeId={storeId} /></TabsContent>
        <TabsContent value="team"><TeamTab storeId={storeId} storeName={store.display_name} /></TabsContent>
        {(isOps || user?.role === 'area_manager') && (
          <TabsContent value="compliance"><ComplianceTab storeId={storeId} storeName={store.display_name} /></TabsContent>
        )}
        <TabsContent value="details"><DetailsTab storeId={storeId} store={store} onChanged={refreshData} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ==================== MONTH SELECTOR RICH ==================== */
/** Selettore mese ricco — mostra per ogni mese: nome, budget vendite,
 *  attuale e variance, con pallino semaforo. Riusato sia nel DashboardTab
 *  che nel BudgetTab (refactor 2026-05-08 — idea_0d76ebfb1cb2 + idea_501d4f8316d1). */
function MonthSelectorRich({ value, onChange, monthsData = [], testIdPrefix = 'month-rich' }) {
  // monthsData: array di { month: 1..12, budget, actual } (le chiavi non
  // trovate finiscono a null).
  const byMonth = useMemo(() => {
    const map = {};
    monthsData.forEach((d) => { if (d && d.month) map[d.month] = d; });
    return map;
  }, [monthsData]);
  return (
    <div className="bg-white border border-slate-200 mb-6" data-testid={`${testIdPrefix}-strip`}>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 divide-x divide-y divide-slate-100">
        {MONTHS.map((mName, i) => {
          const m = i + 1;
          const entry = byMonth[m] || {};
          const bVal = entry.budget;
          const aVal = entry.actual;
          const variance = aVal != null && bVal != null ? aVal - bVal : null;
          const isSelected = m === value;
          const dot = (aVal == null || bVal == null) ? 'bg-slate-300'
            : aVal >= bVal ? 'bg-emerald-600'
            : aVal >= bVal * 0.95 ? 'bg-amber-500'
            : 'bg-red-600';
          // Display logic (fix 2026-06-04 — bug: prima la tile mostrava il budget
          // come numero grande, in contraddizione con la hero in alto che usa
          // l'actual). Ora il numero principale è l'ACTUAL quando presente.
          // Per i mesi futuri/non importati (no actual) ripieghiamo sul budget
          // come "target", così la tile resta significativa.
          const hasActual = aVal != null;
          const primary = hasActual ? aVal : bVal;
          const primaryLabel = hasActual ? null : (bVal != null ? 'Bgt' : null);
          const secondaryValue = hasActual ? bVal : null;
          return (
            <button
              key={m}
              type="button"
              onClick={() => onChange?.(m)}
              className={`p-3 text-left transition-colors ${isSelected ? 'bg-slate-900 text-white' : 'hover:bg-slate-50/60'}`}
              data-testid={`${testIdPrefix}-${m}`}
              aria-pressed={isSelected}
            >
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${dot} flex-shrink-0`} />
                <span className={`text-[10px] uppercase tracking-wider font-semibold ${isSelected ? 'text-slate-300' : 'text-slate-400'}`}>{mName.substring(0, 3)}</span>
                {primaryLabel && (
                  <span className={`text-[9px] uppercase tracking-wider font-semibold ml-auto ${isSelected ? 'text-amber-300' : 'text-amber-700'}`}>
                    {primaryLabel}
                  </span>
                )}
              </div>
              <p className={`font-heading text-base font-bold mt-0.5 ${primary == null ? (isSelected ? 'text-slate-500' : 'text-slate-300') : ''}`}>
                {primary != null ? `€${Math.round(primary).toLocaleString('it-IT')}` : '—'}
              </p>
              {secondaryValue != null && (
                <p className={`text-[10px] mt-0.5 ${isSelected ? 'text-slate-400' : 'text-slate-500'}`}>
                  Bgt: €{Math.round(secondaryValue).toLocaleString('it-IT')}
                </p>
              )}
              {variance != null && (
                <p className={`text-[10px] font-medium ${variance >= 0 ? (isSelected ? 'text-emerald-300' : 'text-emerald-700') : (isSelected ? 'text-red-300' : 'text-red-700')}`}>
                  {variance > 0 ? '+' : ''}{fmt(variance, 'currency')}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}


/* ==================== DASHBOARD TAB ==================== */
function DashboardTab({ data, month, year, quarter, annual, periodLabel, period, storeId, onPeriodChange, isOps, store }) {
  const aiParams = useMemo(() => {
    const p = { year };
    if (annual) p.annual = true;
    else if (quarter) p.quarter = quarter;
    else p.month = month;
    return p;
  }, [month, year, quarter, annual]);
  const aiCacheFetcher = useCallback(() => aiApi.storeInsights(storeId, { ...aiParams, cache_only: true }), [storeId, aiParams]);
  const aiGenerateFetcher = useCallback(() => aiApi.storeInsights(storeId, { ...aiParams, force: false }), [storeId, aiParams]);
  const aiForceFetcher = useCallback(() => aiApi.storeInsights(storeId, { ...aiParams, force: true }), [storeId, aiParams]);
  const aiPdfFetcher = useCallback(() => aiApi.downloadStorePdf(storeId, aiParams), [storeId, aiParams]);
  const { kpis, profitability, costs_summary, monthly_trend } = data;
  const groupedKpis = {};
  kpis.forEach(k => { const g = k.type || 'other'; if (!groupedKpis[g]) groupedKpis[g] = []; groupedKpis[g].push(k); });
  const typeLabels = { revenue: 'Ricavi', efficiency: 'Efficienza', client: 'Clienti', quality: 'Qualit\u00e0', count: 'Volumi', capacity: 'Capacit\u00e0' };
  const isMonthMode = !quarter && !annual;

  // KPI usati nell'Hero strip in cima
  const heroMetrics = useMemo(() => {
    const find = (key) => kpis.find(k => k.key === key) || {};
    const ts = find('total_sales');
    const occ = find('occupancy_pct');
    const varBudgetPct = (ts.actual != null && ts.budget) ? ((ts.actual - ts.budget) / ts.budget) * 100 : null;
    const varLyPct = (ts.actual != null && ts.last_year) ? ((ts.actual - ts.last_year) / ts.last_year) * 100 : null;
    return {
      revenue: ts.actual, budget: ts.budget, ly: ts.last_year,
      varBudget: ts.var_budget, varBudgetPct,
      varLy: ts.var_ly, varLyPct,
      occActual: occ.actual, occBudget: occ.budget, occLy: occ.last_year,
    };
  }, [kpis]);

  // Consistenza annuale: quanti mesi con dato (budget+actual) sono "in target"
  // (attuale >= budget). Calcolato sul `monthly_trend` totale del Total Sales.
  // Mostra anche il miglior e peggior mese tracciato.
  const targetSummary = useMemo(() => {
    const tracked = [];
    let onTarget = 0;
    let best = null;
    let worst = null;
    (monthly_trend || []).forEach((m) => {
      const b = m?.budget || 0;
      const a = m?.actual || 0;
      if (b > 0 && a > 0) {
        const pct = ((a - b) / b) * 100;
        tracked.push({ idx: m.month, pct });
        if (pct >= 0) onTarget += 1;
        if (best == null || pct > best.pct) best = { idx: m.month, pct };
        if (worst == null || pct < worst.pct) worst = { idx: m.month, pct };
      }
    });
    return { onTarget, tracked: tracked.length, best, worst };
  }, [monthly_trend]);

  // Data per il MonthSelectorRich: mappa monthly_trend (un record per mese)
  // sui campi richiesti dal componente.
  const monthsData = useMemo(
    () => (monthly_trend || []).map(m => ({
      month: m.month,
      budget: m.budget ?? null,
      actual: m.actual ?? null,
    })),
    [monthly_trend]
  );

  // Empty-state: il negozio non ha mai ricevuto un budget total_sales importato.
  // Si manifesta sia nel periodo corrente (ts.budget === null) sia in quelli
  // annuali se il monthly_trend non ha MAI un valore di budget > 0.
  const hasAnyBudgetTS = (monthly_trend || []).some(m => (m.budget || 0) > 0);
  const showNoBudgetBanner = !hasAnyBudgetTS;

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-6" data-testid="dashboard-tab-content">
      {showNoBudgetBanner && (
        <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-4 flex items-start gap-3" data-testid="no-budget-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-amber-900">Budget vendite non importato per questo negozio</p>
            <p className="text-amber-800 mt-0.5">
              Lo scheletro (ore disponibili, ticket medio, occupazione) è stato inizializzato dal Setup globale, ma i valori di fatturato budget sono mancanti. Importa il file XLSX budget oppure compila i valori dal tab "Budget".
            </p>
          </div>
          <Link to="/import" className="text-xs uppercase tracking-wider bg-amber-900 text-white px-3 py-1.5 hover:bg-amber-800 whitespace-nowrap" data-testid="no-budget-banner-import-link">
            Vai a Import
          </Link>
        </div>
      )}
      {/* Hero strip — stessa cifra stilistica della Panoramica/Area */}
      <div className="bg-slate-900 text-white p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Vendite totali</p>
            <p className="font-heading text-2xl md:text-4xl font-bold mt-1" data-testid="store-hero-revenue">{fmt(heroMetrics.revenue, 'currency')}</p>
            <p className="text-xs text-slate-400 mt-1">{periodLabel}</p>
          </div>
          <StoreHeroDelta label="vs Budget" value={heroMetrics.varBudget} pct={heroMetrics.varBudgetPct} base={heroMetrics.budget} />
          <StoreHeroDelta label="vs Anno Prec." value={heroMetrics.varLy} pct={heroMetrics.varLyPct} base={heroMetrics.ly} />
          {(() => {
            const occ = heroMetrics.occActual;
            const dB = (occ != null && heroMetrics.occBudget != null) ? occ - heroMetrics.occBudget : null;
            const dLy = (occ != null && heroMetrics.occLy != null) ? occ - heroMetrics.occLy : null;
            return (
              <div data-testid="store-hero-occupancy">
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Tasso di occupazione</p>
                <p className="font-heading text-2xl md:text-4xl font-bold mt-1">{occ != null ? `${occ.toFixed(1)}%` : '—'}</p>
                <p className="text-xs text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  {dB != null && (
                    <span className={SEMAPHORE_TEXT[semaphoreFromPp(dB)]}>
                      vs Budget {dB > 0 ? '+' : ''}{dB.toFixed(1)}pp
                    </span>
                  )}
                  {dLy != null && (
                    <span className={SEMAPHORE_TEXT[semaphoreFromPp(dLy)]}>
                      vs A.P. {dLy > 0 ? '+' : ''}{dLy.toFixed(1)}pp
                    </span>
                  )}
                  {dB == null && dLy == null && <span>{periodLabel}</span>}
                </p>
              </div>
            );
          })()}
        </div>
      </div>
      {isMonthMode && (
        <MonthSelectorRich
          value={month}
          onChange={(m) => onPeriodChange?.(String(m))}
          monthsData={monthsData}
          testIdPrefix="dashboard-month-btn"
        />
      )}
      {/* Strip "Mesi a target" — visibile sempre (anche in vista trimestrale
          o annuale): aggregato di consistenza del negozio sui 12 mesi del
          Total Sales (refactor 2026-05-08, idea_0d76ebfb1cb2). */}
      {targetSummary.tracked > 0 && (
        <div
          className="bg-white border border-slate-200 mb-6 px-5 py-3 flex items-center flex-wrap gap-x-6 gap-y-2"
          data-testid="dashboard-on-target-strip"
        >
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-slate-400" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Mesi a target</span>
            <span className="font-heading text-lg font-bold tabular-nums" data-testid="dashboard-on-target-value">
              <span className={targetSummary.onTarget >= targetSummary.tracked / 2 ? 'text-emerald-700' : 'text-red-700'}>
                {targetSummary.onTarget}
              </span>
              <span className="text-slate-400">/{targetSummary.tracked}</span>
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 ml-auto flex-wrap">
            {targetSummary.best && (
              <span data-testid="dashboard-on-target-best">
                <span className="text-emerald-600 font-semibold">↑ Miglior mese</span>{' '}
                {MONTHS[targetSummary.best.idx - 1].slice(0, 3)}{' '}
                <span className="text-emerald-700 font-mono">{targetSummary.best.pct > 0 ? '+' : ''}{targetSummary.best.pct.toFixed(1)}%</span>
              </span>
            )}
            {targetSummary.worst && targetSummary.best && targetSummary.worst.idx !== targetSummary.best.idx && (
              <span data-testid="dashboard-on-target-worst">
                <span className="text-red-600 font-semibold">↓ Peggior mese</span>{' '}
                {MONTHS[targetSummary.worst.idx - 1].slice(0, 3)}{' '}
                <span className="text-red-700 font-mono">{targetSummary.worst.pct > 0 ? '+' : ''}{targetSummary.worst.pct.toFixed(1)}%</span>
              </span>
            )}
          </div>
        </div>
      )}
      {!isMonthMode && (() => {
        const ts = kpis.find(k => k.key === 'total_sales') || {};
        const actual = ts.actual;
        const ly = ts.last_year;
        const varLy = (actual != null && ly != null && ly !== 0)
          ? ((actual - ly) / ly) * 100
          : null;
        return (
          <div className="bg-white border border-slate-200 mb-6 px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="period-banner">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">Visualizzazione aggregata:</span>
                <span className="text-sm font-semibold text-slate-900">{periodLabel}</span>
                <span className="text-xs text-slate-400">({annual ? '12 mesi' : '3 mesi'})</span>
              </div>
              {actual != null && (
                <div className="flex items-center gap-3 pl-4 border-l border-slate-200" data-testid="period-ytd-summary">
                  <div>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 block">Vendite</span>
                    <span className="text-sm font-bold text-slate-900 font-mono">{fmt(actual, 'currency')}</span>
                  </div>
                  {varLy != null && (
                    <div>
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 block">vs {year - 1}</span>
                      <span className={`text-sm font-bold font-mono ${varLy >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="period-var-ly">
                        {varLy > 0 ? '+' : ''}{varLy.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => onPeriodChange?.(String(month || (quarter - 1) * 3 + 1))}
              className="text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-black border border-slate-200 px-3 py-1.5 hover:border-black transition-colors"
              data-testid="back-to-month-btn"
            >
              Torna al mese
            </button>
          </div>
        );
      })()}
      {/* Andamento mensile in posizione prominente (sostituisce il vecchio
          HealthTrendChart — refactor 2026-05-08, idea_0d76ebfb1cb2). */}
      <AnnualSparkline
        trend={monthly_trend}
        year={year}
        subtitle="Vendite mensili — Attuale vs Budget vs Anno Prec. + Forecast (linea tratteggiata grigia)"
        forecastTooltip="Forecast: proiezione del totale vendite per il mese corrente e quello successivo, basata sulla media degli stessi giorni della settimana delle ultime 8 settimane. Si interrompe quando lo storico è troppo corto per essere affidabile."
        testId="store-annual-sparkline"
      />

      {/* Health score + Report AI affiancati (refactor layout 2026-05-18,
       *  richiesta utente). HealthScoreHeader era prima dentro StoreKpiPanel,
       *  ora viene mostrato qui per stare a fianco del Report AI. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="health-and-ai-row">
        <HealthScoreHeader kpis={kpis} periodLabel={periodLabel} />
        <AIInsightsPanel
          cacheFetcher={aiCacheFetcher}
          generateFetcher={aiGenerateFetcher}
          forceFetcher={aiForceFetcher}
          pdfFetcher={aiPdfFetcher}
          currentSignature={data?.ai_signature}
          scope="store"
          entityId={[storeId, String(year), annual ? 'YEAR' : quarter ? `Q${quarter}` : `M${month}`].join('_')}
          storeId={storeId}
          title="Report AI"
          compact
          autoOpen
        />
      </div>

      {/* Ticket aperti del negozio (idea_e48f953be864 2026-05-19).
       *  Mostra i guasti/manutenzioni attive così l'AM in dashboard vede
       *  subito se serve agire. Compatta - massimo 5 visibili + link a /assistance. */}
      <OpenTicketsForStore storeId={storeId} />

      <StoreKpiPanel
        kpis={kpis}
        periodLabel={periodLabel}
        hideHealthHeader
        renderDesktopTable={() => (
          <div className="bg-white border border-slate-200" data-testid="kpi-table">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="font-heading text-lg font-semibold tracking-tight">KPI Dettagliati — {periodLabel}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead><tr><th className="min-w-[200px]">KPI</th><th className="text-right">Attuale</th><th className="text-right">Budget</th><th className="text-right">Var. Budget</th><th className="text-right">Anno Prec.</th><th className="text-right">Var. A.P.</th></tr></thead>
                <tbody>
                  {Object.entries(groupedKpis).map(([type, items]) => (
                    <React.Fragment key={type}>
                      <tr><td colSpan={6} className="!bg-slate-50 !py-1.5 !px-4"><span className="text-xs uppercase tracking-wider font-semibold text-slate-400">{typeLabels[type] || type}</span></td></tr>
                      {items.map(k => (
                        <tr key={k.key} data-testid={`kpi-row-${k.key}`}>
                          <td>
                            <span className="font-medium text-slate-900">{k.label}</span>
                            {KPI_FORMULAS[k.key] && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="ml-1.5 inline-flex align-middle opacity-50 hover:opacity-100 focus:opacity-100 focus:outline-none"
                                    aria-label={`Formula ${k.label}`}
                                    data-testid={`kpi-info-${k.key}`}
                                  >
                                    <Info className="w-3 h-3" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  <span className="font-medium">{k.label}</span>: {KPI_FORMULAS[k.key]}
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {k.source_type === 'manual' && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 ml-2">manuale</span>}
                          </td>
                          <td className="num font-semibold text-slate-900">{fmt(k.actual, k.format)}</td>
                          <td className="num text-slate-500">{fmt(k.budget, k.format)}</td>
                          <VarCell value={k.var_budget} format={k.format} semaforo={k.sem_budget} />
                          <td className="num text-slate-500">{fmt(k.last_year, k.format)}</td>
                          <VarCell value={k.var_ly} format={k.format} semaforo={k.sem_ly} />
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-3">
          <ProfitabilitySection profitability={profitability} periodLabel={periodLabel} title="Redditività" testId="profitability-section" />
          <p className="text-xs text-slate-400 px-1 mt-2">{costs_summary.count} voci di costo — totale: {fmt(costs_summary.total, 'currency')}</p>
        </div>
      </div>

      {/* Recensioni Google (idea utente 2026-05-15). Mostra rating +
       *  count + ultime 5 recensioni testuali (se Atmosphere SKU attiva).
       *  Place ID viene dal documento store sul backend; refresh manuale
       *  visibile solo all'OPS. Il titolo della sezione vive ora DENTRO
       *  la card stessa (componente), non sopra come h3 esterno. */}
      <GoogleReviewsPanel
        storeId={storeId}
        placeId={store?.google_place_id}
        canRefresh={isOps}
      />

      {/* Note e piani d'azione */}
      <StoreNotesSection storeId={storeId} />
    </div>
    </TooltipProvider>
  );
}

/* ==================== STORE NOTES ==================== */
function StoreNotesSection({ storeId }) {
  const [notes, setNotes] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [deletingNote, setDeletingNote] = useState(null);
  // Filtro 3-tab: 'all' | 'ai' | 'manual'. Default 'all' — gli AM partono
  // sempre dalla vista completa, poi possono restringere su "Da AI" per
  // usarle come check-list di review settimanale (caso d'uso descritto
  // nell'idea backlog).
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const r = await storeNotesApi.list(storeId);
      setNotes(r.data);
      setForbidden(false);
    } catch (err) {
      if (err.response?.status === 403) { setForbidden(true); setNotes([]); }
      else { setNotes([]); }
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      await storeNotesApi.create(storeId, t);
      setText('');
      await load();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const saveEdit = async (noteId) => {
    const t = editingText.trim();
    if (!t) return;
    try {
      await storeNotesApi.update(storeId, noteId, t);
      setEditingId(null);
      setEditingText('');
      await load();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    }
  };

  const confirmDelete = async () => {
    if (!deletingNote) return;
    try {
      await storeNotesApi.remove(storeId, deletingNote.note_id);
      setDeletingNote(null);
      await load();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
      setDeletingNote(null);
    }
  };

  if (forbidden) {
    return (
      <div className="bg-white border border-slate-200 p-6 mt-6" data-testid="store-notes-forbidden">
        <h3 className="font-heading text-base font-semibold mb-1 flex items-center gap-2">
          <StickyNote className="w-4 h-4" /> Note e piani d'azione
        </h3>
        <p className="text-sm text-slate-500">
          Questa sezione è riservata a Operations Manager e Area Manager assegnati a questo negozio.
        </p>
      </div>
    );
  }

  if (notes === null) return null;

  // Conteggi per i tab. Trattiamo `source !== 'ai_action_plan'` come manuale
  // (copre sia 'manual' esplicito sia note legacy senza campo `source`).
  const aiCount = notes.filter(n => n.source === 'ai_action_plan').length;
  const manualCount = notes.length - aiCount;
  const filteredNotes = filter === 'ai'
    ? notes.filter(n => n.source === 'ai_action_plan')
    : filter === 'manual'
      ? notes.filter(n => n.source !== 'ai_action_plan')
      : notes;

  const FilterTab = ({ value, label, count, testId }) => (
    <button
      type="button"
      onClick={() => setFilter(value)}
      className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
        filter === value
          ? 'border-slate-900 text-slate-900'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
      data-testid={testId}
    >
      {label}
      <span className={`tabular-nums px-1.5 py-0.5 text-[10px] font-semibold ${
        filter === value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
      }`}>
        {count}
      </span>
    </button>
  );

  return (
    <div className="bg-white border border-slate-200 mt-6" data-testid="store-notes-section">
      <div className="px-5 py-4 border-b border-slate-200">
        <h3 className="font-heading text-base font-semibold flex items-center gap-2">
          <StickyNote className="w-4 h-4" /> Note e piani d'azione
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Appunta idee, criticità, azioni di miglioramento. Visibile a Operations Manager e Area Manager di questo negozio.
        </p>
      </div>

      {/* Composer */}
      <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Scrivi una nota o un piano d'azione..."
          className="rounded-none resize-none min-h-[80px] bg-white"
          data-testid="note-composer-input"
        />
        <div className="flex justify-end mt-2">
          <Button
            onClick={addNote}
            disabled={saving || !text.trim()}
            className="bg-black text-white hover:bg-slate-900 rounded-none"
            data-testid="note-composer-submit"
          >
            <Plus className="w-4 h-4 mr-2" /> {saving ? 'Salvataggio...' : 'Aggiungi nota'}
          </Button>
        </div>
      </div>

      {/* Filtro a tab — mostrato solo se ci sono note (sennò è rumore visivo) */}
      {notes.length > 0 && (
        <div className="px-5 pt-3 border-b border-slate-200 flex gap-1" data-testid="notes-filter-tabs">
          <FilterTab value="all" label="Tutte" count={notes.length} testId="notes-filter-all" />
          <FilterTab value="ai" label="Da AI" count={aiCount} testId="notes-filter-ai" />
          <FilterTab value="manual" label="Manuali" count={manualCount} testId="notes-filter-manual" />
        </div>
      )}

      {/* Timeline */}
      {notes.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-400" data-testid="notes-empty">
          Nessuna nota per questo negozio.
        </div>
      ) : filteredNotes.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-400" data-testid="notes-filter-empty">
          Nessuna nota in questa categoria.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {filteredNotes.map(n => (
            <li key={n.note_id} className="px-5 py-4" data-testid={`note-item-${n.note_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1 flex-wrap">
                    <span className="font-medium text-slate-700">{n.author_name}</span>
                    <span>·</span>
                    <span>{new Date(n.created_at).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                    {n.updated_at && n.updated_at !== n.created_at && (
                      <>
                        <span>·</span>
                        <span className="italic">modificata {new Date(n.updated_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</span>
                      </>
                    )}
                    {n.source === 'ai_action_plan' && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold border bg-violet-50 text-violet-700 border-violet-200"
                        data-testid={`note-ai-badge-${n.note_id}`}
                      >
                        <Sparkles className="w-2.5 h-2.5" />
                        Da AI
                      </span>
                    )}
                  </div>
                  {editingId === n.note_id ? (
                    <>
                      <Textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        className="rounded-none resize-none min-h-[80px]"
                        data-testid={`note-edit-input-${n.note_id}`}
                      />
                      <div className="flex gap-2 mt-2">
                        <Button onClick={() => saveEdit(n.note_id)} size="sm" className="rounded-none bg-black text-white hover:bg-slate-900" data-testid={`note-edit-save-${n.note_id}`}>
                          <Save className="w-3 h-3 mr-1" /> Salva
                        </Button>
                        <Button onClick={() => { setEditingId(null); setEditingText(''); }} size="sm" variant="outline" className="rounded-none">
                          Annulla
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{n.text}</p>
                  )}
                </div>
                {editingId !== n.note_id && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => { setEditingId(n.note_id); setEditingText(n.text); }}
                      className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                      title="Modifica"
                      data-testid={`note-edit-btn-${n.note_id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setDeletingNote(n)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50"
                      title="Elimina"
                      data-testid={`note-delete-btn-${n.note_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Confirm delete dialog */}
      <AlertDialog open={!!deletingNote} onOpenChange={(open) => { if (!open) setDeletingNote(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="note-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la nota?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione è irreversibile. La nota verrà rimossa definitivamente.
              {deletingNote && (
                <span className="block mt-3 p-3 bg-slate-50 border border-slate-200 text-sm text-slate-700 italic">
                  "{deletingNote.text.slice(0, 180)}{deletingNote.text.length > 180 ? '…' : ''}"
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="note-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-none bg-red-600 hover:bg-red-700" data-testid="note-delete-confirm">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ==================== BUDGET TAB ==================== */
function BudgetTab({ storeId, storeName, year, month, onMonthChange, onChanged }) {
  const [budgetData, setBudgetData] = useState(null);
  const [kpiDefs, setKpiDefs] = useState([]);
  const [setup, setSetup] = useState(null);
  const selectedMonth = month;
  const setSelectedMonth = onMonthChange;
  const [availableHoursInput, setAvailableHoursInput] = useState('');
  const [monthValues, setMonthValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [budgetYear, setBudgetYear] = useState(year);

  const load = useCallback(async () => {
    const [bRes, kRes, sRes] = await Promise.all([
      budgetsApi.getStoreYear(storeId, budgetYear),
      kpiApi.definitions(),
      budgetsApi.getSetup(),
    ]);
    setBudgetData(bRes.data);
    setKpiDefs(kRes.data);
    setSetup(sRes.data);
  }, [storeId, budgetYear]);

  useEffect(() => { load(); }, [load]);

  // Sync the editable inputs (available_hours, avg_ticket, review_count) with the selected month
  useEffect(() => {
    if (budgetData) {
      const stored = budgetData?.months?.[selectedMonth] || {};
      const ah = stored.available_hours;
      setAvailableHoursInput(ah != null ? String(ah) : '');
      setMonthValues({
        avg_ticket: stored.avg_ticket != null ? stored.avg_ticket : '',
        review_count: stored.review_count != null ? stored.review_count : '',
      });
    }
  }, [selectedMonth, budgetData]);

  // Derive all KPI values live from total_sales (imported/stored) + available_hours (input)
  const derived = useMemo(() => {
    if (!setup) return {};
    const stored = budgetData?.months?.[selectedMonth] || {};
    const ts = stored.total_sales || 0;
    const ah = availableHoursInput === '' ? (stored.available_hours ?? setup.available_hours ?? 500) : Number(availableHoursInput);
    const svc_pct = (setup.service_revenue_pct || 0) / 100;
    const prod_pct = (setup.product_revenue_pct || 0) / 100;
    const service_revenue = Math.round(ts * svc_pct * 100) / 100;
    const product_revenue = Math.round(ts * prod_pct * 100) / 100;
    const avg_svc = setup.avg_service_value || 1;
    const avg_prod = setup.avg_product_value || 1;
    const per_hour = ah > 0 ? Math.round((ts / ah) * 100) / 100 : 0;
    // review_count: input store-level (se vuoto, fallback al setup globale)
    const rcStored = stored.review_count;
    const rcInput = monthValues.review_count;
    const rc = (rcInput === '' || rcInput == null)
      ? (rcStored ?? setup.review_count ?? 0)
      : Number(rcInput);
    return {
      total_sales: ts,
      service_revenue,
      product_revenue,
      services_count: avg_svc > 0 ? Math.round(service_revenue / avg_svc) : 0,
      products_count: avg_prod > 0 ? Math.round(product_revenue / avg_prod) : 0,
      avg_ticket: per_hour,
      occupancy_pct: setup.occupancy_pct ?? 0,
      review_count: rc,
      new_client_pct: setup.new_client_pct ?? 0,
      returning_client_rate: setup.returning_client_rate ?? 0,
      memberships_sold: setup.memberships_sold ?? 0,
      available_hours: ah,
      hourly_revenue: per_hour,
    };
  }, [setup, budgetData, selectedMonth, availableHoursInput, monthValues.review_count]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const stored = budgetData?.months?.[selectedMonth] || {};
      const ah = availableHoursInput === '' ? null : Number(availableHoursInput);
      const at = monthValues.avg_ticket === '' || monthValues.avg_ticket == null ? null : Number(monthValues.avg_ticket);
      const rc = monthValues.review_count === '' || monthValues.review_count == null ? null : Number(monthValues.review_count);
      await budgetsApi.save({
        store_id: storeId, month: selectedMonth, year: budgetYear,
        values: {
          total_sales: stored.total_sales ?? 0,
          available_hours: ah,
          avg_ticket: at,
          review_count: rc,
        },
      });
      await load();
      onChanged && onChanged();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  if (!budgetData || !setup) return <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />;

  const editableKeys = new Set(['available_hours', 'avg_ticket', 'review_count']);

  // Data per il MonthSelectorRich condiviso col DashboardTab.
  const monthSelectorData = MONTHS.map((_, i) => ({
    month: i + 1,
    budget: budgetData.months?.[i + 1]?.total_sales ?? null,
    actual: budgetData.actuals?.[i + 1]?.total_sales ?? null,
  }));

  return (
    <div data-testid="store-budget-tab" className="space-y-6">
      {/* Compact header: titolo + selettore anno + link a Setup globale.
          L'Hero strip è stato rimosso (refactor 2026-05-08, idea_501d4f8316d1):
          le metriche "Mesi a target" e l'aggregato vendite ora vivono
          nel Dashboard Tab, dove sono visibili anche a chi non ha accesso
          al Budget Tab (OPS-only). */}
      <div className="bg-white border border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3" data-testid="budget-tab-header">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Budget {storeName}</p>
          <p className="font-heading text-lg font-semibold text-slate-900">Anno {budgetYear}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={String(budgetYear)} onValueChange={(v) => setBudgetYear(Number(v))}>
            <SelectTrigger className="w-28 rounded-none" data-testid="budget-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Link
            to="/settings?tab=budget-setup"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:border-black hover:text-black px-3 py-2 transition-colors"
            data-testid="budget-tab-settings-link"
          >
            <Settings className="w-3.5 h-3.5" /> Setup globale
            <ExternalLink className="w-3 h-3 opacity-60" />
          </Link>
        </div>
      </div>

      {/* Monthly overview — usa il MonthSelectorRich condiviso */}
      <div>
        <div className="px-1 mb-2 flex items-center justify-between flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Riepilogo Vendite Totali per mese</span>
          <div className="flex items-center gap-3 text-[10px] text-slate-400">
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-600 inline-block" /> attuale &gt; budget</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-600 inline-block" /> attuale &lt; budget</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-slate-300 inline-block" /> nessun dato</span>
          </div>
        </div>
        <MonthSelectorRich
          value={selectedMonth}
          onChange={setSelectedMonth}
          monthsData={monthSelectorData}
          testIdPrefix="budget-month-btn"
        />
      </div>

      {/* Monthly KPI detail editor */}
      <div className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-heading text-base font-semibold">Budget KPI — {MONTHS[selectedMonth - 1]} {budgetYear}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              <span className="font-medium">Vendite totali</span> vengono dall'import Excel • <span className="font-medium">Ore disponibili</span> e <span className="font-medium">Ticket medio</span> sono override manuali opzionali per questo negozio • tutti gli altri KPI sono calcolati in automatico dal Setup Budget globale
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="save-store-budget-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Salvataggio...' : 'Salva Budget'}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th className="min-w-[200px]">KPI</th>
                <th className="text-right min-w-[140px]">Budget</th>
                <th className="text-right">Attuale</th>
                <th className="text-right">Varianza</th>
              </tr>
            </thead>
            <tbody>
              {kpiDefs.map(kd => {
                const bVal = derived[kd.key];
                const aVal = budgetData.actuals?.[selectedMonth]?.[kd.key];
                const variance = aVal != null && bVal != null ? aVal - bVal : null;
                const isEditable = editableKeys.has(kd.key);
                const monthSources = budgetData.sources?.[selectedMonth] || {};
                const isManualOverride = isEditable && monthSources[kd.key] === 'budget';
                return (
                  <tr key={kd.key} data-testid={`budget-kpi-row-${kd.key}`} className="hover:bg-slate-50/40 transition-colors">
                    <td>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{kd.label}</span>
                        {isManualOverride && (
                          <span
                            className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5"
                            data-testid={`manual-badge-${kd.key}`}
                          >Manuale</span>
                        )}
                        {kd.key === 'total_sales' && (
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5">Import</span>
                        )}
                        {!isEditable && kd.key !== 'total_sales' && (
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5">Auto</span>
                        )}
                        {isEditable && !isManualOverride && (
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 bg-white border border-dashed border-slate-300 px-1.5 py-0.5">Editabile</span>
                        )}
                      </div>
                    </td>
                    <td className="text-right">
                      {isEditable ? (
                        <Input
                          type="number" step="any"
                          className="w-36 rounded-none text-right font-mono ml-auto"
                          value={kd.key === 'available_hours' ? availableHoursInput : (monthValues[kd.key] ?? '')}
                          onChange={(e) => kd.key === 'available_hours' ? setAvailableHoursInput(e.target.value) : setMonthValues(v => ({...v, [kd.key]: e.target.value}))}
                          placeholder="—"
                          data-testid={`budget-input-${kd.key}`}
                        />
                      ) : (
                        <span className="font-mono text-slate-700">{bVal != null ? fmt(bVal, kd.format) : '—'}</span>
                      )}
                    </td>
                    <td className="num text-slate-500">{aVal != null ? fmt(aVal, kd.format) : '-'}</td>
                    <td className="num">
                      {variance != null ? (
                        <span className={`font-medium ${variance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {variance > 0 ? '+' : ''}{fmt(variance, kd.format)}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ==================== COSTS TAB ==================== */
function CostsTab({ storeId, storeName, onChanged }) {
  const [costs, setCosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterMonth, setFilterMonth] = useState('all');
  const [form, setForm] = useState({ category: '', cost_type: 'recurring', amount: '', description: '', month: '', year: '2026', start_date: '', end_date: '', notes: '' });
  const [submitError, setSubmitError] = useState('');

  const canSubmit = Boolean(form.cost_type && form.category && form.amount && Number(form.amount) > 0 && form.month && form.year);

  const loadCosts = useCallback(async () => {
    const [cRes, catRes] = await Promise.all([costsApi.list({ store_id: storeId }), costsApi.categories()]);
    setCosts(cRes.data);
    setCategories(catRes.data);
    setLoading(false);
  }, [storeId]);

  useEffect(() => { loadCosts(); }, [loadCosts]);

  const handleCreate = async () => {
    setSubmitError('');
    // Tutti i campi obbligatori tranne descrizione/note
    const missing = [];
    if (!form.cost_type) missing.push('tipo');
    if (!form.category) missing.push('categoria');
    if (!form.amount || Number(form.amount) <= 0) missing.push('importo');
    if (!form.month) missing.push('mese');
    if (!form.year) missing.push('anno');
    if (missing.length) {
      setSubmitError(`Campi obbligatori mancanti: ${missing.join(', ')}.`);
      return;
    }
    try {
      const data = { store_id: storeId, ...form, amount: Number(form.amount), month: Number(form.month), year: Number(form.year) };
      await costsApi.create(data);
      setDialogOpen(false);
      setForm({ category: '', cost_type: 'recurring', amount: '', description: '', month: '', year: '2026', start_date: '', end_date: '', notes: '' });
      loadCosts();
      onChanged && onChanged();
    } catch (e) {
      setSubmitError(e.response?.data?.detail || e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo costo?')) return;
    await costsApi.delete(id);
    loadCosts();
    onChanged && onChanged();
  };

  const filtered = filterMonth === 'all' ? costs : costs.filter(c => c.month === Number(filterMonth));
  const totalCosts = filtered.reduce((s, c) => s + (c.amount || 0), 0);

  // Group by category
  const byCategory = {};
  filtered.forEach(c => {
    const cat = c.category || 'Altro';
    byCategory[cat] = (byCategory[cat] || 0) + c.amount;
  });

  if (loading) return <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />;

  return (
    <div data-testid="store-costs-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-semibold">Costi — {storeName}</h2>
        <div className="flex items-center gap-3">
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-40 rounded-none" data-testid="cost-month-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i mesi</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="add-store-cost-btn">
                <Plus className="w-4 h-4 mr-2" /> Aggiungi costo
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle className="font-heading">Nuovo costo — {storeName}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <Select value={form.cost_type} onValueChange={(v) => setForm({ ...form, cost_type: v, category: '' })}>
                    <SelectTrigger className="rounded-none" data-testid="cost-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recurring">Ricorrente</SelectItem>
                      <SelectItem value="variable">Variabile mensile</SelectItem>
                      <SelectItem value="one_off">Una tantum</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="rounded-none" data-testid="cost-category-select"><SelectValue placeholder="Categoria" /></SelectTrigger>
                    <SelectContent>
                      {categories.filter(c => c.cost_type === form.cost_type || form.cost_type === 'variable').map(c => (
                        <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>
                      ))}
                      <SelectItem value="Affitti e spese">Affitti e spese</SelectItem>
                      <SelectItem value="Costo Personale">Costo Personale</SelectItem>
                      <SelectItem value="Costo Prodotti">Costo Prodotti</SelectItem>
                      <SelectItem value="Costo Utenze">Costo Utenze</SelectItem>
                      <SelectItem value="Costo Gestione">Costo Gestione</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Input type="number" step="0.01" placeholder="Importo (€)" className="rounded-none" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cost-amount-input" />
                  <Select value={form.month} onValueChange={(v) => setForm({ ...form, month: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue placeholder="Mese" /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={form.year} onValueChange={(v) => setForm({ ...form, year: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input placeholder="Descrizione (opzionale)" className="rounded-none" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cost-description-input" />
                {submitError && (
                  <p className="text-xs text-red-600" data-testid="cost-form-error">{submitError}</p>
                )}
                <p className="text-[11px] text-slate-400">Tutti i campi sono obbligatori tranne la Descrizione.</p>
                <Button
                  onClick={handleCreate}
                  disabled={!canSubmit}
                  className="w-full bg-black text-white hover:bg-slate-900 rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-cost-btn"
                >
                  Salva costo
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary by category */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {Object.entries(byCategory).sort((a, b) => b[1] - a[1]).map(([cat, total]) => (
          <div key={cat} className="bg-white border border-slate-200 p-4">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{cat}</span>
            <p className="font-heading text-lg font-bold mt-1">{fmt(total, 'currency')}</p>
          </div>
        ))}
        <div className="bg-slate-900 text-white p-4">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-300">Totale</span>
          <p className="font-heading text-lg font-bold mt-1">{fmt(totalCosts, 'currency')}</p>
          <p className="text-xs text-slate-400">{filtered.length} voci</p>
        </div>
      </div>

      {/* Costs Table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400">
          <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Nessun costo per questo periodo</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Categoria</th>
                <th>Tipo</th>
                <th>Periodo</th>
                <th>Descrizione</th>
                <th className="text-right">Importo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.cost_id} data-testid={`cost-row-${c.cost_id}`}>
                  <td className="font-medium">{c.category}</td>
                  <td>
                    <span className={`text-xs uppercase tracking-wider font-medium px-2 py-0.5 border ${
                      c.cost_type === 'recurring' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      c.cost_type === 'variable' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-purple-50 text-purple-700 border-purple-200'
                    }`}>{c.cost_type === 'recurring' ? 'Ricorrente' : c.cost_type === 'variable' ? 'Variabile' : 'Una tantum'}</span>
                  </td>
                  <td className="text-sm text-slate-500">
                    {c.month && c.year ? `${MONTHS[c.month - 1]} ${c.year}` : c.start_date ? new Date(c.start_date).toLocaleDateString('it-IT') : '-'}
                  </td>
                  <td className="text-slate-500 text-sm">{c.description || '-'}</td>
                  <td className="num font-semibold">{fmt(c.amount, 'currency')}</td>
                  <td>
                    <button onClick={() => handleDelete(c.cost_id)} className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-500" data-testid={`delete-cost-${c.cost_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ==================== DETAILS TAB ==================== */
function DetailsTab({ storeId, store, onChanged }) {
  const [allAMs, setAllAMs] = useState([]);
  const [storeAMIds, setStoreAMIds] = useState(store.area_manager_ids || []);
  const [area, setArea] = useState(store.area || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { areaManagersApi.list().then(r => setAllAMs(r.data)); }, []);

  const toggleAM = (amId) => setStoreAMIds(prev => prev.includes(amId) ? prev.filter(id => id !== amId) : [...prev, amId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await storesApi.update(storeId, { area_manager_ids: storeAMIds, area_manager_id: storeAMIds[0] || null, area });
      onChanged && onChanged();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="store-details-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-semibold">Dettagli negozio</h2>
        <Button onClick={handleSave} disabled={saving} className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="save-store-details-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Salvataggio...' : 'Salva modifiche'}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 p-5">
          <h3 className="font-heading text-base font-semibold mb-1">Area Manager assegnati</h3>
          <p className="text-xs text-slate-500 mb-4">Seleziona uno o pi&ugrave; Area Manager</p>
          <div className="space-y-2">
            {allAMs.map(am => {
              const selected = storeAMIds.includes(am.area_manager_id);
              return (
                <button key={am.area_manager_id} onClick={() => toggleAM(am.area_manager_id)}
                  className={`w-full text-left px-4 py-3 border transition-colors flex items-center justify-between ${selected ? 'border-black bg-slate-900 text-white' : 'border-slate-200 hover:border-slate-400'}`}
                  data-testid={`am-toggle-${am.area_manager_id}`}>
                  <div>
                    <span className="font-medium text-sm">{am.name}</span>
                    {am.email && <span className={`text-xs ml-2 ${selected ? 'text-slate-400' : 'text-slate-400'}`}>{am.email}</span>}
                  </div>
                  {selected && <span className="text-xs bg-white text-black px-2 py-0.5">Assegnato</span>}
                </button>
              );
            })}
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 p-5">
            <h3 className="font-heading text-base font-semibold mb-1">Area geografica</h3>
            <p className="text-xs text-slate-500 mb-3">Assegna questo negozio a un'area specifica</p>
            <Input value={area} onChange={(e) => setArea(e.target.value)} placeholder="Es. Nord Italia, Lombardia, Emilia..." className="rounded-none" data-testid="store-area-input" />
          </div>
          <StoreStationsEditor storeId={storeId} />
          <div className="bg-white border border-slate-200 p-5">
            <h3 className="font-heading text-base font-semibold mb-3">Informazioni negozio</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Nome</span><span className="font-medium">{store.display_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Codice</span><span className="font-mono">{store.code}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Brand</span><span>{store.brand_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Fresha</span><span className="text-xs text-slate-400 max-w-[250px] text-right">{store.fresha_reference}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Stato</span><span className={store.active ? 'text-emerald-600' : 'text-red-500'}>{store.active ? 'Attivo' : 'Inattivo'}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==================== SHARED ==================== */
function KpiCard({ kpi }) {
  const sem = kpi.sem_budget || 'neutral';
  const Icon = KPI_ICONS[kpi.type] || TrendingUp;
  return (
    <div className={`border p-4 semaforo-${sem}`} data-testid={`kpi-card-${kpi.key}`}>
      <div className="flex items-center gap-2 mb-2"><Icon className="w-3.5 h-3.5 opacity-60" /><span className="text-[11px] uppercase tracking-wider font-semibold opacity-70">{kpi.label}</span></div>
      <p className="font-heading text-xl font-bold">{fmt(kpi.actual, kpi.format)}</p>
      {kpi.var_budget != null && (
        <div className="flex items-center gap-1 mt-1 text-xs">
          {kpi.var_budget > 0 ? <TrendingUp className="w-3 h-3" /> : kpi.var_budget < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
          <span>vs Budget: {kpi.var_budget > 0 ? '+' : ''}{fmt(kpi.var_budget, kpi.format)}</span>
        </div>
      )}
    </div>
  );
}

function StoreHeroDelta({ label, value, pct, base }) {
  if (value == null) {
    return (
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</p>
        <p className="font-heading text-2xl md:text-4xl font-bold text-slate-500 mt-1">—</p>
      </div>
    );
  }
  const sem = semaphoreFromPct(pct) || (value >= 0 ? 'green' : 'red');
  const tone = SEMAPHORE_TEXT[sem];
  const Icon = value >= 0 ? TrendingUp : TrendingDown;
  return (
    <div data-testid={`store-hero-delta-${label?.replace(/\s+/g, '-').toLowerCase()}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</p>
      <p className={`font-heading text-2xl md:text-4xl font-bold mt-1 flex items-center gap-2 ${tone}`}>
        <Icon className="w-6 h-6 opacity-70" />
        {value > 0 ? '+' : ''}{fmt(value, 'currency')}
      </p>
      <p className="text-xs text-slate-400 mt-1">
        {pct != null && <span className={tone}>{pct > 0 ? '+' : ''}{pct.toFixed(1)}%</span>}
        {base != null && <> · base {fmt(base, 'currency')}</>}
      </p>
    </div>
  );
}

function ProfitRow({ label, value, highlight, badge }) {
  const color = value > 0 ? 'text-emerald-700' : value < 0 ? 'text-red-700' : 'text-slate-500';
  const badgeColors = {
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    slate: 'text-slate-600 bg-slate-100 border-slate-200',
  };
  return (
    <div className={`flex items-center justify-between ${highlight ? 'font-semibold' : ''}`}>
      <span className="flex items-center gap-2 min-w-0">
        <span className={`text-sm ${highlight ? 'text-slate-900' : 'text-slate-600'} truncate`}>{label}</span>
        {badge && (
          <span
            className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border ${badgeColors[badge.color] || badgeColors.slate} shrink-0`}
            data-testid={`one-off-badge-${label.replace(/\s+/g, '-')}`}
          >{badge.text}</span>
        )}
      </span>
      <span className={`font-mono text-sm ${color}`}>{fmt(Math.abs(value || 0), 'currency')}{value < 0 ? ' -' : ''}</span>
    </div>
  );
}

/* ==================== ANNUAL SPARKLINE ==================== */
/** 12-month line chart of Total Sales (Actual / Budget / Last Year).
 *  Stessa cifra stilistica del `TrendChart` in Panoramica: header con titolo
 *  uppercase + sottotitolo, area 280px, legenda interna Recharts, linee con
 *  Attuale solido nero, Budget viola tratteggiato, Anno Prec. blu tratteggiato. */
export function AnnualSparkline({ trend, year, subtitle, forecastTooltip, testId }) {
  const data = (trend || []).map((r) => ({
    name: MONTHS[r.month - 1].slice(0, 3),
    Attuale: r.actual ?? r.value ?? null,
    Budget: r.budget ?? null,
    'Anno Prec.': r.last_year ?? null,
    Forecast: r.forecast ?? null,
  }));
  const hasAny = data.some((d) => d.Attuale != null || d.Budget != null || d['Anno Prec.'] != null);
  const hasForecast = data.some((d) => d.Forecast != null);
  // Indicatore "oggi" sul mese corrente — solo se stiamo guardando l'anno
  // in corso (es. nel 2026 evidenziamo il mese di oggi; per il 2025 non
  // mostriamo nulla). Allinea visivamente il chart al `ForecastLineChart`
  // daily e aiuta a leggere il forecast (idea_0d76ebfb1cb2, 2026-05-08).
  const now = new Date();
  const todayMonthAbbr =
    year === now.getFullYear() ? MONTHS[now.getMonth()].slice(0, 3) : null;
  return (
    <div className="bg-white border border-slate-200" data-testid={testId || 'annual-sparkline'}>
      <div className="px-5 py-4 border-b border-slate-200">
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-700">
          Andamento mensile {year}
        </h2>
        {subtitle && (
          <div className="flex items-start gap-1.5 mt-1">
            <p className="text-xs text-slate-400">{subtitle}</p>
            {hasForecast && forecastTooltip && (
              <Info
                className="w-3 h-3 text-slate-400 mt-0.5 cursor-help flex-shrink-0"
                title={forecastTooltip}
                aria-label="Come è calcolato il forecast"
                data-testid="annual-sparkline-forecast-info"
              />
            )}
          </div>
        )}
      </div>
      <div className="p-3" style={{ height: 280 }}>
        {hasAny ? (
          <ResponsiveContainer width="100%" height="100%">
            {/* margin.top aumentato (8 → 28) per non clippare il label
                "oggi" della ReferenceLine — stesso fix applicato al
                ForecastLineChart in CalendarTab (2026-05-08). */}
            <LineChart data={data} margin={{ top: 28, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} stroke="#cbd5e1" />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                stroke="#cbd5e1"
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
              />
              <RTooltip
                content={<SeriesTooltipContent fmt={(v) => fmt(v, 'currency')} />}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {todayMonthAbbr && (
                <ReferenceLine
                  x={todayMonthAbbr}
                  stroke="#475569"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  label={{
                    value: 'oggi',
                    position: 'insideTopRight',
                    offset: 8,
                    fontSize: 10,
                    fontWeight: 600,
                    fill: '#475569',
                  }}
                  data-testid="annual-sparkline-today-line"
                />
              )}
              <Line type="monotone" dataKey="Attuale" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="Budget" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="Anno Prec." stroke="#1e3a8a" strokeWidth={2} strokeDasharray="2 4" dot={false} connectNulls={false} isAnimationActive={false} />
              {hasForecast && (
                <Line
                  type="monotone"
                  dataKey="Forecast"
                  stroke="#64748b"
                  strokeWidth={2}
                  strokeDasharray="3 3"
                  dot={{ r: 2.5, fill: '#fff', stroke: '#64748b', strokeWidth: 1.5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-slate-400 text-center py-12">Nessun dato disponibile</p>
        )}
      </div>
    </div>
  );
}


// ============================================================
// InventoryTab — lista oggetti del negozio (read-only inline)
// + link alla scheda dettaglio per gestione completa
// ============================================================
function InventoryTab({ storeId, storeName }) {
  const [items, setItems] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      inventoryApi.listItems({ store_id: storeId }).catch(() => ({ data: [] })),
      inventoryApi.storeDashboard(storeId).catch(() => ({ data: null })),
    ]).then(([li, dash]) => {
      if (!alive) return;
      setItems(li.data || []);
      setKpi(dash.data || null);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [storeId]);

  return (
    <div className="space-y-4" data-testid="store-inventory-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-xl font-bold">Inventario di {storeName}</h3>
          <p className="text-sm text-slate-500">
            {items.length} oggetti registrati ·{' '}
            <Link to={`/inventory?store=${storeId}`} className="underline">
              vai al magazzino completo
            </Link>
          </p>
        </div>
      </div>

      {kpi && kpi.total_items > 0 && <InventoryMiniDashboard kpi={kpi} />}

      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Package className="w-10 h-10 mx-auto text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">
              Nessun oggetto è stato ancora assegnato a questo negozio.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Codice</th>
                <th className="px-3 py-2 text-left">Categoria · Modello</th>
                <th className="px-3 py-2 text-left">Assegnato</th>
                <th className="px-3 py-2 text-right">Data acquisto</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.item_id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <Link
                      to={`/inventory/${it.item_id}`}
                      state={{
                        backTo: `/stores/${storeId}?tab=inventory`,
                        backLabel: `Inventario ${storeName || 'negozio'}`,
                      }}
                      className="font-mono font-semibold hover:underline"
                    >
                      {it.code}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{it.category_name}</span>
                    {it.model && <span className="text-slate-500"> · {it.model}</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{it.assigned_user_id ? '✓' : '—'}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{it.purchase_date || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================
// InventoryMiniDashboard — KPI Magazzino per il singolo negozio.
// Mostrato sopra la lista nel tab Inventario, visibile a tutti i
// ruoli che vedono il tab (incluso store_user).
// ============================================================
function InventoryMiniDashboard({ kpi }) {
  const mstat = kpi.maintenance_status || { recent: 0, old: 0, none: 0, total: 0 };
  const tot = mstat.total || 1;
  const recentPct = Math.round((mstat.recent / tot) * 100);
  const oldPct = Math.round((mstat.old / tot) * 100);
  const nonePct = Math.max(0, 100 - recentPct - oldPct);
  const photoCoveragePct = kpi.total_items > 0
    ? Math.round((kpi.photos_present / kpi.total_items) * 100) : 0;
  const fmtEUR = (v) => `€${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const oldestYears = kpi.oldest ? (kpi.oldest.age_days / 365.25).toFixed(1) : null;

  return (
    <section
      className="bg-white border border-slate-200 rounded-lg p-5 space-y-5"
      data-testid="store-inventory-mini-dashboard"
    >
      {/* Top KPI strip: 4 numeri compatti */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100 rounded-md overflow-hidden">
        <MiniKpi label="Attrezzi" value={kpi.total_items} testId="kpi-total-items" />
        <MiniKpi label="Valore totale" value={fmtEUR(kpi.total_value)} testId="kpi-total-value" />
        <MiniKpi
          label="Manutenzioni"
          value={kpi.maintenance_count}
          sub={kpi.maintenance_cost > 0 ? `${fmtEUR(kpi.maintenance_cost)} cumulato` : null}
          testId="kpi-maintenance"
        />
        <MiniKpi
          label="Ticket aperti"
          value={kpi.open_tickets}
          tone={kpi.open_tickets > 0 ? 'warn' : 'ok'}
          testId="kpi-open-tickets"
        />
      </div>

      {/* Avvisi manutenzione consigliata (solo se la categoria ha la regola e ci sono allarmi) */}
      {kpi.maintenance_alerts?.total > 0 && (
        <div
          className="flex items-center gap-3 px-4 py-3 border bg-amber-50 border-amber-300 text-amber-900"
          data-testid="kpi-maintenance-alerts"
        >
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-semibold">{kpi.maintenance_alerts.total} attrezz{kpi.maintenance_alerts.total === 1 ? 'o' : 'i'} da rivedere</span>
            <span className="text-xs ml-2 opacity-80">
              {kpi.maintenance_alerts.overdue > 0 && (
                <span className="text-red-700">{kpi.maintenance_alerts.overdue} in ritardo</span>
              )}
              {kpi.maintenance_alerts.overdue > 0 && kpi.maintenance_alerts.due > 0 && ' · '}
              {kpi.maintenance_alerts.due > 0 && `${kpi.maintenance_alerts.due} consigliat${kpi.maintenance_alerts.due === 1 ? 'a' : 'e'}`}
            </span>
          </div>
        </div>
      )}

      {/* Mid section: 3 colonne — categoria, stato manutenzione, età */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Per categoria */}
        <div className="space-y-2" data-testid="kpi-by-category">
          <h5 className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-500">
            Per categoria
          </h5>
          {kpi.by_category.length === 0 ? (
            <p className="text-xs text-slate-400 italic">—</p>
          ) : (
            <ul className="space-y-1.5">
              {kpi.by_category.slice(0, 5).map(c => {
                const pct = Math.round((c.count / kpi.total_items) * 100);
                return (
                  <li key={c.category_id} className="text-xs">
                    <div className="flex justify-between mb-0.5">
                      <span className="text-slate-700">{c.name}</span>
                      <span className="text-slate-500 font-mono">{c.count}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-slate-900" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
              {kpi.by_category.length > 5 && (
                <li className="text-[11px] text-slate-400 italic">
                  +{kpi.by_category.length - 5} altre
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Stato manutenzione (segmented bar verde/ambra/grigio) */}
        <div className="space-y-2" data-testid="kpi-maintenance-status">
          <h5 className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-500">
            Stato manutenzione
          </h5>
          <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
            {mstat.recent > 0 && (
              <div className="h-full bg-emerald-500" style={{ width: `${recentPct}%` }} title={`${mstat.recent} recenti`} />
            )}
            {mstat.old > 0 && (
              <div className="h-full bg-amber-500" style={{ width: `${oldPct}%` }} title={`${mstat.old} vecchie`} />
            )}
            {mstat.none > 0 && (
              <div className="h-full bg-slate-300" style={{ width: `${nonePct}%` }} title={`${mstat.none} mai`} />
            )}
          </div>
          <ul className="text-xs space-y-1 mt-1.5">
            <li className="flex justify-between">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />Recente (≤90gg)
              </span>
              <span className="font-mono">{mstat.recent}</span>
            </li>
            <li className="flex justify-between">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500" />Vecchia (&gt;90gg)
              </span>
              <span className="font-mono">{mstat.old}</span>
            </li>
            <li className="flex justify-between">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-slate-300" />Mai
              </span>
              <span className="font-mono">{mstat.none}</span>
            </li>
          </ul>
        </div>

        {/* Età + copertura foto */}
        <div className="space-y-3" data-testid="kpi-age-photos">
          <div>
            <h5 className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-500">
              Età parco attrezzi
            </h5>
            <p className="text-2xl font-bold mt-1">
              {kpi.avg_age_years}
              <span className="text-sm font-medium text-slate-500 ml-1">anni medi</span>
            </p>
            {kpi.oldest && (
              <p className="text-[11px] text-slate-500 mt-0.5 font-mono">
                Più vecchio: {kpi.oldest.code} · {oldestYears} anni
              </p>
            )}
          </div>
          <div>
            <h5 className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-500">
              Copertura foto
            </h5>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-lg font-bold">{photoCoveragePct}%</span>
              <span className="text-[11px] text-slate-500">
                {kpi.photos_present}/{kpi.total_items} con foto
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1.5">
              <div
                className={`h-full ${photoCoveragePct >= 80 ? 'bg-emerald-500' : photoCoveragePct >= 50 ? 'bg-amber-500' : 'bg-slate-400'}`}
                style={{ width: `${photoCoveragePct}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniKpi({ label, value, sub, tone, testId }) {
  const toneCls = tone === 'warn' ? 'text-amber-700' : tone === 'ok' ? 'text-slate-900' : 'text-slate-900';
  return (
    <div className="bg-white p-4" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${toneCls}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}


// ============================================================
// Open Tickets per Store (idea_e48f953be864 — 2026-05-19)
// Mostra fino a 5 ticket attivi del negozio direttamente nella Dashboard
// tab. Click su singola riga apre il dettaglio in /assistance/{id}.
// ============================================================
const TICKET_STATUS = {
  open: { label: 'Aperto', tone: 'bg-red-50 text-red-700 border-red-200' },
  in_progress: { label: 'In lavorazione', tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  waiting_parts: { label: 'In attesa ricambi', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved: { label: 'Risolto', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};
const TICKET_PRIORITY = {
  low: 'bg-slate-100 text-slate-700',
  normal: 'bg-slate-100 text-slate-700',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800 font-semibold',
};
const TICKET_PRI_LABEL = { low: 'Bassa', normal: 'Normale', high: 'Alta', urgent: 'Urgente' };

function OpenTicketsForStore({ storeId }) {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState(null);

  useEffect(() => {
    let mounted = true;
    ticketsApi.list({ store_id: storeId })
      .then(r => { if (mounted) setTickets(r.data || []); })
      .catch(() => { if (mounted) setTickets([]); });
    return () => { mounted = false; };
  }, [storeId]);

  if (tickets === null) return null;

  const visible = tickets.slice(0, 5);
  const more = tickets.length - visible.length;
  const goAll = () => navigate(`/assistance?store_id=${storeId}`);

  if (tickets.length === 0) {
    return (
      <div
        className="bg-white border border-slate-200 px-4 md:px-5 py-3 flex items-center justify-between gap-3 flex-wrap"
        data-testid="open-tickets-empty"
      >
        <div className="flex items-center gap-2 text-sm text-slate-500 min-w-0">
          <Wrench className="w-4 h-4 text-slate-400 shrink-0" />
          Nessun ticket di assistenza aperto su questo negozio.
        </div>
        <button
          onClick={() => navigate('/assistance', { state: { openCreate: true, prefill: { store_id: storeId } } })}
          className="text-xs text-slate-700 hover:text-slate-900 underline underline-offset-2"
          data-testid="open-tickets-create"
        >
          Apri un ticket
        </button>
      </div>
    );
  }

  return (
    <section className="bg-white border border-slate-200" data-testid="open-tickets-section">
      <div className="px-4 md:px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
        <h3 className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 flex items-center gap-2">
          <Wrench className="w-3.5 h-3.5 text-violet-600" />
          Ticket aperti
          <span className="ml-1 px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-mono">
            {tickets.length}
          </span>
        </h3>
        <button
          onClick={goAll}
          className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2"
          data-testid="open-tickets-see-all"
        >
          Vedi tutti →
        </button>
      </div>
      <ul className="divide-y divide-slate-100">
        {visible.map(t => {
          const status = TICKET_STATUS[t.status] || TICKET_STATUS.open;
          const ageDays = Math.floor((Date.now() - new Date(t.created_at).getTime()) / 86400000);
          const ageLabel = ageDays === 0 ? 'oggi' : ageDays === 1 ? '1 giorno fa' : `${ageDays} giorni fa`;
          return (
            <li key={t.ticket_id}>
              <button
                onClick={() => navigate(`/assistance/${t.ticket_id}`)}
                className="w-full text-left px-4 md:px-5 py-3 hover:bg-slate-50 transition flex items-start gap-3"
                data-testid={`open-ticket-${t.ticket_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${TICKET_PRIORITY[t.priority]}`}>
                      {TICKET_PRI_LABEL[t.priority]}
                    </span>
                    <p className="text-sm font-semibold text-slate-900 truncate">{t.title}</p>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    {t.item_code && <span className="font-mono">{t.item_code}</span>}
                    {t.assignee_email && <span>→ {t.assignee_email}</span>}
                    <span>aperto {ageLabel}</span>
                  </p>
                </div>
                <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-medium ${status.tone} shrink-0`}>
                  {status.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {more > 0 && (
        <button
          onClick={goAll}
          className="w-full px-4 md:px-5 py-2.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-t border-slate-200 transition"
          data-testid="open-tickets-more"
        >
          + altri {more} ticket aperti
        </button>
      )}
    </section>
  );
}



// ============================================================
// TeamTab — Squadra del negozio (idea_914110128c38, 2026-05-26)
// Mostra i dipendenti assegnati allo store (primario o secondario).
// Field-level visibility lato backend: AM/SM/SU non vedranno campi sensibili.
// ============================================================

const TEAM_ROLE_LABELS = {
  operations_manager: 'Operations Manager',
  hr_manager: 'HR Manager',
  area_manager: 'Area Manager',
  store_manager: 'Store Manager',
  barber_senior: 'Barbiere Senior',
  barber_junior: 'Barbiere Junior',
  apprentice: 'Apprendista',
  receptionist: 'Reception',
  other: 'Altro',
};
const TEAM_STATUS_TONE = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  on_leave: 'bg-blue-50 text-blue-700 border-blue-200',
  sick: 'bg-amber-50 text-amber-700 border-amber-200',
  maternity: 'bg-pink-50 text-pink-700 border-pink-200',
  terminated: 'bg-slate-100 text-slate-500 border-slate-200',
};

function TeamTab({ storeId, storeName }) {
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.list({ store_id: storeId });
      setTeam(r.data || []);
    } catch (e) {
      setTeam([]);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-12"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="team-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-900">Squadra · {storeName}</h2>
          <p className="text-slate-500 text-sm mt-0.5">{team.length} {team.length === 1 ? 'persona' : 'persone'} assegnate a questo negozio</p>
        </div>
        <button
          onClick={() => navigate(`/staff?store_id=${storeId}`)}
          className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"
          data-testid="team-open-staff-page"
        >
          Apri pagina Staff <ExternalLink className="w-3.5 h-3.5" />
        </button>
      </div>

      {team.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-12 text-center" data-testid="team-empty">
          <IdCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">Nessun dipendente assegnato</p>
          <p className="text-slate-500 text-sm mt-1">Aggiungi un dipendente dalla pagina Staff per popolare la squadra di questo negozio.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-grid">
          {team.map(p => (
            <button
              key={p.staff_id}
              type="button"
              onClick={() => navigate(`/staff/${p.staff_id}`, { state: { backTo: `/stores/${storeId}?tab=team` } })}
              className="text-left bg-white border border-slate-200 hover:border-slate-400 transition-colors rounded-sm p-4 flex flex-col gap-3"
              data-testid={`team-card-${p.staff_id}`}
            >
              <div className="flex items-start gap-3">
                {p.photo_url ? (
                  <img src={p.photo_url} alt="" className="w-12 h-12 rounded-full object-cover bg-slate-100" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm">
                    {`${(p.first_name || '?')[0] || ''}${(p.last_name || '?')[0] || ''}`.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{p.first_name} {p.last_name}</p>
                  <p className="text-xs text-slate-500 truncate">{TEAM_ROLE_LABELS[p.role_in_store] || p.role_in_store}</p>
                </div>
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm shrink-0 ${TEAM_STATUS_TONE[p.status] || TEAM_STATUS_TONE.active}`}>
                  {p.status === 'active' ? 'Attivo' : p.status}
                </span>
              </div>
              <div className="text-xs text-slate-600 space-y-1">
                {p.email_work && (
                  <div className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{p.email_work}</span>
                  </div>
                )}
                {p.phone_work && (
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{p.phone_work}</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
